import * as React from "react";
import { ChangeEvent, useRef, useState } from "react";
import Button from "@mui/material/Button";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { CircularProgress, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { validateStringField } from "../../../utils/Validation";
import { IToolboxBuild, ToolboxBuildArch } from "../../../services/registry/ToolboxBuildService";
export type ToolboxBuildFormType = "new" | "edit";

export interface ToolboxBuildFormData {
  id: string;
  name: string;
  version: string;
  branch: string;
  arch?: ToolboxBuildArch;
  file?: File;
  commitHash?: string;
}

export interface ToolboxBuildFormProps {
  visible: boolean;
  type: ToolboxBuildFormType;
  initialData?: IToolboxBuild;
  onDismiss: () => void;
  onAccept: (data: ToolboxBuildFormData) => Promise<void>;
}

export const ToolboxBuildFormField = (props: Omit<TextFieldProps, "variant">) => {
  return <TextField autoFocus margin="dense" fullWidth variant="standard" {...props} />;
};

export const ToolboxBuildForm = ({ visible, type, initialData, onDismiss, onAccept }: ToolboxBuildFormProps) => {
  const [loading, setLoading] = useState(false);
  const isNewMode = type === "new";

  const refName = useRef<TextFieldProps>(null);
  const refVersion = useRef<TextFieldProps>(null);
  const refBranch = useRef<TextFieldProps>(null);
  const refCommitHash = useRef<TextFieldProps>(null);

  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [versionError, setVersionError] = useState<string | undefined>(undefined);
  const [branchError, setBranchError] = useState<string | undefined>(undefined);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [arch, setArch] = useState<ToolboxBuildArch>(initialData?.arch || ToolboxBuildArch.X86_64);

  const dialogCloseHandler = (event: unknown, reason: string) => {
    if (reason !== "backdropClick") {
      onDismiss();
    }
  };

  const validateName = (value: string) => validateStringField(value, setNameError, 3, 999);
  const validateVersion = (value: string) => validateStringField(value, setVersionError, 1, 999);
  const validateBranch = (value: string) => validateStringField(value, setBranchError, 1, 999);

  const nameChangeHandler = (event: ChangeEvent<HTMLInputElement>) => nameError && validateName(event.target.value);
  const versionChangeHandler = (event: ChangeEvent<HTMLInputElement>) => versionError && validateVersion(event.target.value);
  const branchChangeHandler = (event: ChangeEvent<HTMLInputElement>) => branchError && validateBranch(event.target.value);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleArchChange = (event: React.ChangeEvent<{ name?: string; value: unknown }> | any) => {
    setArch(event.target.value as ToolboxBuildArch);
  };

  const okButtonClickHandler = async () => {
    if (!refName.current || !refVersion.current || !refBranch.current) {
      return;
    }

    const id = initialData?.id ?? "";
    const name = validateName(refName.current.value as string);
    const version = validateVersion(refVersion.current.value as string);
    const branch = validateBranch(refBranch.current.value as string);
    if (!name || !version || !branch) {
      return;
    }

    // Basic data for both edit and new modes
    const formData: ToolboxBuildFormData = {
      id,
      name,
      version,
      branch,
    };

    // Add upload-specific data for new mode
    if (isNewMode) {
      if (!selectedFile) {
        setNameError("Please select a file to upload");
        return;
      }

      formData.arch = arch;
      formData.file = selectedFile;

      if (refCommitHash.current && refCommitHash.current.value) {
        formData.commitHash = refCommitHash.current.value as string;
      }
    }

    setLoading(true);
    await onAccept(formData);
    setLoading(false);

    // Reset state after submission
    if (isNewMode) {
      setSelectedFile(null);
      setArch(ToolboxBuildArch.X86_64);
    }
  };

  const dialogTitle = isNewMode ? "Upload New Toolbox Build" : "Edit Toolbox Build";
  const buttonText = isNewMode ? "Upload" : "Save";

  return (
    <Dialog open={visible} onClose={dialogCloseHandler}>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        {loading ? (
          <CircularProgress />
        ) : (
          <>
            <DialogContentText>
              {isNewMode ? "Please provide details for the new toolbox build:" : "Please update the information below:"}
            </DialogContentText>
            <ToolboxBuildFormField
              inputRef={refName}
              id="name"
              label="Name"
              type="text"
              defaultValue={initialData?.name}
              error={nameError !== undefined}
              helperText={nameError}
              onChange={nameChangeHandler}
            />
            <ToolboxBuildFormField
              inputRef={refVersion}
              id="version"
              label="Version"
              type="text"
              defaultValue={initialData?.version}
              error={versionError !== undefined}
              helperText={versionError}
              onChange={versionChangeHandler}
            />
            <ToolboxBuildFormField
              inputRef={refBranch}
              id="branch"
              label="Branch"
              type="text"
              defaultValue={initialData?.branch}
              error={branchError !== undefined}
              helperText={branchError}
              onChange={branchChangeHandler}
            />

            {isNewMode && (
              <>
                <FormControl fullWidth margin="dense" variant="standard">
                  <InputLabel id="arch-select-label">Architecture</InputLabel>
                  <Select
                    labelId="arch-select-label"
                    name="arch"
                    value={arch}
                    label="Architecture"
                    onChange={handleArchChange}
                  >
                    <MenuItem value={ToolboxBuildArch.X86_64}>{ToolboxBuildArch.X86_64}</MenuItem>
                    <MenuItem value={ToolboxBuildArch.ARM64}>{ToolboxBuildArch.ARM64}</MenuItem>
                  </Select>
                </FormControl>
                <ToolboxBuildFormField
                  inputRef={refCommitHash}
                  id="commitHash"
                  label="Commit Hash (optional)"
                  type="text"
                  defaultValue={initialData?.commitHash}
                />
                <input
                  accept=".zip"
                  style={{ display: "none" }}
                  id="raised-button-file"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="raised-button-file">
                  <Button variant="outlined" component="span" fullWidth sx={{ mt: 2 }}>
                    {selectedFile ? selectedFile.name : "Select Build File (.zip)"}
                  </Button>
                </label>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onDismiss} color="error" disabled={loading}>
          Cancel
        </Button>
        <Button onClick={okButtonClickHandler} disabled={loading}>
          {buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
